import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, GroundOverlay, Circle, Marker } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../utils/googleMaps';
import { X, TrendingUp, Satellite, Calendar, AlertTriangle, ChevronLeft, ChevronRight, Info, Loader2, RefreshCw, Navigation, MapPin, Plus, Check, Camera, Upload, HelpCircle, Droplets, Leaf, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const mapContainerStyle = { width: '100%', height: '100%' };

// Vegetation index definitions with icons and colors - UPDATED with proper legends per user spec
const INDEX_CONFIG = {
  ndvi: {
    name: 'NDVI',
    fullName: 'Normalized Difference Vegetation Index',
    icon: Leaf,
    color: '#22c55e',
    description: 'Measures vegetation greenness and density. Higher values indicate healthier, denser vegetation.',
    formula: '(NIR - Red) / (NIR + Red)',
    useCases: 'General crop health monitoring, biomass estimation, yield prediction',
    limitations: 'Can saturate in dense canopy, affected by soil background in sparse vegetation',
    clamp: [0.0, 0.9],
    bands: [0.15, 0.35, 0.55, 0.75],
    legendTitle: 'Vegetation Vigor (NDVI)',
    palette: ['#8B1E1E', '#D8572A', '#F2C14E', '#2E8B57', '#0B5D3A'],
    labels: ['Bare/Residue', 'Low vigor', 'Moderate', 'High', 'Very high'],
    statusText: (value) => {
      if (value < 0.35) return 'Low vigor';
      if (value < 0.55) return 'Moderate vigor';
      return 'Good/High vigor';
    }
  },
  evi: {
    name: 'EVI',
    fullName: 'Enhanced Vegetation Index',
    icon: TrendingUp,
    color: '#14b8a6',
    description: 'Reduces atmospheric and soil background effects. Better than NDVI in dense vegetation.',
    formula: '2.5 × (NIR - Red) / (NIR + 6×Red - 7.5×Blue + 1)',
    useCases: 'High-biomass areas, tropical/dense crops, atmospheric correction needed',
    limitations: 'More complex calculation, may be less stable in sparse vegetation',
    clamp: [0.0, 0.8],
    bands: [0.10, 0.30, 0.50, 0.65],
    legendTitle: 'Dense Canopy Vigor (EVI)',
    palette: ['#8B1E1E', '#D8572A', '#F2C14E', '#2E8B57', '#0B5D3A'],
    labels: ['Bare', 'Low', 'Moderate', 'High', 'Very high'],
    statusText: (value) => {
      if (value < 0.30) return 'Low vigor';
      if (value < 0.50) return 'Moderate vigor';
      return 'High vigor';
    }
  },
  ndmi: {
    name: 'NDMI',
    fullName: 'Normalized Difference Moisture Index',
    icon: Droplets,
    color: '#3b82f6',
    description: 'Detects water content in vegetation canopy. Higher values indicate more moisture.',
    formula: '(NIR - SWIR) / (NIR + SWIR)',
    useCases: 'Water stress detection, irrigation planning, drought monitoring',
    limitations: 'Measures LEAF water content, not soil moisture. Affected by canopy density.',
    clamp: [-0.4, 0.6],
    bands: [-0.1, 0.1, 0.25, 0.4],
    legendTitle: 'Canopy Moisture Proxy (NDMI)',
    palette: ['#8B4513', '#D2691E', '#F0E68C', '#87CEEB', '#1E90FF'],
    labels: ['Very dry', 'Dry', 'Normal', 'Moist', 'Very moist'],
    statusText: (value) => {
      if (value < -0.1) return 'Very dry';
      if (value < 0.1) return 'Dry';
      if (value < 0.25) return 'Normal moisture';
      if (value < 0.4) return 'Moist';
      return 'Very moist';
    }
  },
  ndre: {
    name: 'NDRE',
    fullName: 'Normalized Difference Red Edge',
    icon: Activity,
    color: '#8b5cf6',
    description: 'More sensitive to chlorophyll content than NDVI, especially in mid-to-late season.',
    formula: '(NIR - RedEdge) / (NIR + RedEdge)',
    useCases: 'Nitrogen status assessment, chlorophyll monitoring, late-season crop health',
    limitations: 'Less effective in early growth stages, requires red edge band',
    clamp: [0.05, 0.55],
    bands: [0.15, 0.25, 0.35, 0.45],
    legendTitle: 'Chlorophyll / Red-Edge (NDRE)',
    palette: ['#8B1E1E', '#D8572A', '#F2C14E', '#2E8B57', '#0B5D3A'],
    labels: ['Low chlorophyll', 'Below normal', 'Normal', 'High', 'Very high'],
    statusText: (value) => {
      if (value < 0.20) return 'Low chlorophyll';
      if (value < 0.35) return 'Normal chlorophyll';
      return 'High chlorophyll';
    }
  }
};

const CHECKLIST_ITEMS = [
  { key: 'emergence', label: 'Emergence Issues', icon: '🌱' },
  { key: 'weeds', label: 'Weeds Present', icon: '🌿' },
  { key: 'pests', label: 'Pest Damage', icon: '🐛' },
  { key: 'disease', label: 'Disease Signs', icon: '🦠' },
  { key: 'nutrientDeficiency', label: 'Nutrient Deficiency', icon: '⚡' },
  { key: 'waterStress', label: 'Water Stress', icon: '💧' },
];

const NDVIVisualization = ({ field, ndviReadings, onClose, onRefresh }) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  const [selectedReading, setSelectedReading] = useState(0);
  const [imageData, setImageData] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const mapRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const locationWatchRef = useRef(null);
  
  // Scout mode state
  const [scoutMode, setScoutMode] = useState(false);
  const [pendingScoutPoint, setPendingScoutPoint] = useState(null);
  const [showScoutModal, setShowScoutModal] = useState(false);
  const [creatingPoint, setCreatingPoint] = useState(false);
  const [scoutingPoints, setScoutingPoints] = useState([]);
  
  // Scouting form state
  const [scoutForm, setScoutForm] = useState({
    checklist: {
      emergence: false,
      weeds: false,
      pests: false,
      disease: false,
      nutrientDeficiency: false,
      waterStress: false,
    },
    notes: '',
    photos: []
  });

  // Multi-index state
  const [selectedIndex, setSelectedIndex] = useState('ndvi');
  const [indexObservations, setIndexObservations] = useState({});
  const [showIndexInfo, setShowIndexInfo] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(false);
  const [indexCache, setIndexCache] = useState({}); // Cache for overlay images
  const center = useMemo(() => {
    if (!field?.geometry?.coordinates?.[0]) {
      return { lat: 43.2141, lng: 27.9147 };
    }
    const coords = field.geometry.coordinates[0];
    const lats = coords.map(c => c[1]);
    const lngs = coords.map(c => c[0]);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
    };
  }, [field?.id]);

  // Memoize map options
  const mapOptions = useMemo(() => ({
    mapTypeId: 'satellite',
    mapTypeControl: true,
    streetViewControl: false,
    draggableCursor: scoutMode ? 'crosshair' : undefined,
    clickableIcons: false
  }), [scoutMode]);

  // Memoize polygon paths
  const polygonPaths = useMemo(() => {
    if (!field?.geometry?.coordinates?.[0]) return [];
    return field.geometry.coordinates[0].map(coord => ({
      lat: coord[1],
      lng: coord[0]
    }));
  }, [field?.id]);

  // Load existing scouting points
  useEffect(() => {
    const loadScoutingPoints = async () => {
      try {
        const response = await axios.get(
          `${API}/api/fields/${field.id}/scouting-points`,
          { headers: getAuthHeaders() }
        );
        setScoutingPoints(response.data || []);
      } catch (err) {
        console.log('Could not load scouting points');
      }
    };
    if (field?.id) loadScoutingPoints();
  }, [field?.id]);

  // Watch user location
  useEffect(() => {
    if (navigator.geolocation) {
      locationWatchRef.current = navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    }
    return () => {
      if (locationWatchRef.current) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
    };
  }, []);

  // Helper functions for reading data
  const getReadingDate = (reading) => {
    const dateStr = reading?.acquisition_date || reading?.date || reading?.timestamp;
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const getReadingMean = (reading) => {
    return reading?.mean ?? reading?.value ?? null;
  };

  // Load NDVI image - try to fetch from backend if not present
  useEffect(() => {
    const loadNdviImage = async () => {
      if (!ndviReadings || ndviReadings.length === 0) return;
      
      const reading = ndviReadings[selectedReading];
      if (!reading) return;
      
      // If image_base64 exists, use it
      if (reading.image_base64) {
        setImageData(`data:image/png;base64,${reading.image_base64}`);
        return;
      }
      
      // Try to fetch image from backend
      if (reading.id && field?.id) {
        setLoadingImage(true);
        try {
          const response = await axios.get(
            `${API}/api/ndvi/${field.id}/image/${reading.id}`,
            { headers: getAuthHeaders() }
          );
          if (response.data?.image_base64) {
            setImageData(`data:image/png;base64,${response.data.image_base64}`);
          } else {
            setImageData(null);
          }
        } catch (err) {
          console.log('Could not fetch NDVI image:', err.message);
          setImageData(null);
        } finally {
          setLoadingImage(false);
        }
      } else {
        setImageData(null);
      }
    };
    
    loadNdviImage();
  }, [selectedReading, ndviReadings, field?.id]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    
    // Register click listener using Google Maps native API
    map.addListener('click', (e) => {
      console.log('Native map click detected');
      if (scoutModeRef.current) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setPendingScoutPoint({ lat, lng });
        setShowScoutModal(true);
      }
    });
  }, []);
  
  // Keep a ref to track scout mode for the native listener
  const scoutModeRef = useRef(scoutMode);
  useEffect(() => {
    scoutModeRef.current = scoutMode;
  }, [scoutMode]);

  const handleMyLocation = useCallback(() => {
    if (userLocation && mapRef.current) {
      mapRef.current.panTo({ lat: userLocation.lat, lng: userLocation.lng });
      mapRef.current.setZoom(17);
      toast.success('Centered on your location');
    } else {
      toast.info('Getting location...');
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
            setUserLocation(pos);
            if (mapRef.current) {
              mapRef.current.panTo(pos);
              mapRef.current.setZoom(17);
            }
          },
          () => toast.error('Could not get location')
        );
      }
    }
  }, [userLocation]);

  // Handle map click - open modal with coordinates
  const handleMapClick = useCallback((e) => {
    if (!scoutMode) return;
    
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    // Set coordinates and open modal
    setPendingScoutPoint({ lat, lng });
    setShowScoutModal(true);
  }, [scoutMode]);

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPhotos = [];
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      newPhotos.push({ file, preview });
    }

    setScoutForm(prev => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos]
    }));
  };

  const removePhoto = (index) => {
    setScoutForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  // Create scouting point with full data
  const handleCreateScoutPoint = async () => {
    if (!pendingScoutPoint) return;
    
    setCreatingPoint(true);
    
    try {
      // 1. Create the scouting point
      const response = await axios.post(
        `${API}/api/fields/${field.id}/scouting-points`,
        {
          lat: pendingScoutPoint.lat,
          lng: pendingScoutPoint.lng,
          status: 'open',
          checklist: scoutForm.checklist,
          notes: scoutForm.notes,
          fieldId: field.id,
          farmId: field.farm_id
        },
        { headers: getAuthHeaders() }
      );
      
      const createdPoint = response.data;
      
      // 2. Upload photos if any
      if (scoutForm.photos.length > 0) {
        for (const photo of scoutForm.photos) {
          if (!photo.file) continue;
          
          try {
            const formData = new FormData();
            formData.append('file', photo.file);
            formData.append('pointId', createdPoint.id);
            formData.append('fieldId', field.id);
            
            await axios.post(
              `${API}/api/scouting-points/${createdPoint.id}/photos`,
              formData,
              { 
                headers: { 
                  ...getAuthHeaders(),
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } catch (err) {
            console.error('Failed to upload photo:', err);
          }
        }
      }
      
      // Add to local state
      setScoutingPoints(prev => [createdPoint, ...prev]);
      
      toast.success('Scouting point created! View details in Scouting tab.');
      
      // Reset form and close modal
      setShowScoutModal(false);
      setPendingScoutPoint(null);
      setScoutMode(false);
      setScoutForm({
        checklist: {
          emergence: false,
          weeds: false,
          pests: false,
          disease: false,
          nutrientDeficiency: false,
          waterStress: false,
        },
        notes: '',
        photos: []
      });
    } catch (error) {
      console.error('Create scout point error:', error);
      toast.error('Failed to create scouting point');
    } finally {
      setCreatingPoint(false);
    }
  };

  const handleCancelScoutPoint = () => {
    setShowScoutModal(false);
    setPendingScoutPoint(null);
    // Clean up photo previews
    scoutForm.photos.forEach(p => {
      if (p.preview) URL.revokeObjectURL(p.preview);
    });
    setScoutForm({
      checklist: {
        emergence: false,
        weeds: false,
        pests: false,
        disease: false,
        nutrientDeficiency: false,
        waterStress: false,
      },
      notes: '',
      photos: []
    });
  };

  // Handle NDVI refresh
  const handleRefreshNDVI = async () => {
    setRefreshing(true);
    try {
      await axios.post(
        `${API}/api/ndvi/${field.id}/refresh`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('NDVI refresh started. New data will appear shortly.');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to refresh NDVI');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch specific index
  const handleFetchIndex = async (indexType) => {
    setLoadingIndex(true);
    try {
      const response = await axios.post(
        `${API}/api/ndvi/${field.id}/fetch-index?index_type=${indexType}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data?.success) {
        toast.success(`${indexType.toUpperCase()} data fetched successfully`);
        
        // Fetch the image for this observation
        const obsResponse = await axios.get(
          `${API}/api/ndvi/${field.id}/observations/${response.data.observation_id}`,
          { headers: getAuthHeaders() }
        );
        
        if (obsResponse.data?.image_base64) {
          // Cache the image
          setIndexCache(prev => ({
            ...prev,
            [indexType]: {
              imageData: `data:image/png;base64,${obsResponse.data.image_base64}`,
              observation: obsResponse.data,
              timestamp: Date.now()
            }
          }));
          
          // If this is the selected index, show overlay
          if (indexType === selectedIndex) {
            setImageData(`data:image/png;base64,${obsResponse.data.image_base64}`);
          }
        }
        
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.data?.message || `Failed to fetch ${indexType.toUpperCase()}`);
      }
    } catch (err) {
      toast.error(`Failed to fetch ${indexType.toUpperCase()}: ${err.message}`);
    } finally {
      setLoadingIndex(false);
    }
  };

  // Handle index change with debounced auto-fetch
  const indexChangeTimeoutRef = useRef(null);
  
  const handleIndexChange = async (indexType) => {
    setSelectedIndex(indexType);
    
    // Clear any pending auto-fetch
    if (indexChangeTimeoutRef.current) {
      clearTimeout(indexChangeTimeoutRef.current);
    }
    
    // Check if we have cached data for this index
    const cached = indexCache[indexType];
    if (cached && cached.imageData) {
      setImageData(cached.imageData);
      setShowOverlay(true);
      return;
    }
    
    // For NDVI, use the existing readings
    if (indexType === 'ndvi' && ndviReadings?.length > 0) {
      const reading = ndviReadings[selectedReading];
      if (reading?.image_base64) {
        setImageData(`data:image/png;base64,${reading.image_base64}`);
        setShowOverlay(true);
      }
      return;
    }
    
    // Otherwise auto-fetch with 300ms debounce
    setImageData(null);
    indexChangeTimeoutRef.current = setTimeout(() => {
      handleFetchIndex(indexType);
    }, 300);
  };

  // Navigate readings
  const goToPrevReading = () => {
    if (selectedReading > 0) setSelectedReading(selectedReading - 1);
  };

  const goToNextReading = () => {
    if (selectedReading < ndviReadings.length - 1) setSelectedReading(selectedReading + 1);
  };

  const currentReading = ndviReadings?.[selectedReading];

  // Get NDVI status
  const getNDVIStatus = (mean) => {
    if (mean === null || mean === undefined) return { color: 'gray', label: 'No data' };
    if (mean < 0) return { color: 'red', label: 'Poor' };
    if (mean < 0.2) return { color: 'orange', label: 'Stressed' };
    if (mean < 0.4) return { color: 'yellow', label: 'Moderate' };
    if (mean < 0.6) return { color: 'lightgreen', label: 'Good' };
    return { color: 'green', label: 'Excellent' };
  };

  const status = getNDVIStatus(getReadingMean(currentReading));

  // Calculate bounds
  const getBounds = () => {
    if (!field?.geometry?.coordinates?.[0]) return null;
    const coords = field.geometry.coordinates[0];
    const lats = coords.map(c => c[1]);
    const lngs = coords.map(c => c[0]);
    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    };
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const bounds = getBounds();

  // Get current index config
  const currentIndexConfig = INDEX_CONFIG[selectedIndex];
  const IndexIcon = currentIndexConfig?.icon || Leaf;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-2xl font-semibold flex items-center gap-2">
            <Satellite className="w-6 h-6 text-green-600" />
            {field.name} - Remote Sensing
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Sentinel-2 vegetation indices at 10-20m resolution
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => {
              const newMode = !scoutMode;
              setScoutMode(newMode);
              if (!newMode) {
                setPendingScoutPoint(null);
                setShowScoutModal(false);
              }
              if (newMode) toast.info('Scout mode: Tap on the map to add a point');
            }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${
              scoutMode 
                ? 'bg-amber-500 text-white hover:bg-amber-600' 
                : 'bg-primary text-white hover:bg-primary/90'
            }`}
            data-testid="ndvi-scout-button"
          >
            {scoutMode ? (
              <>
                <X className="w-4 h-4" />
                Cancel Scout
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Point
              </>
            )}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Index Selector Tabs */}
      <div className="flex flex-wrap items-center gap-2 p-2 bg-card rounded-xl border border-border">
        {Object.entries(INDEX_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const isSelected = selectedIndex === key;
          const hasCachedData = key === 'ndvi' ? (ndviReadings?.length > 0) : !!indexCache[key];
          
          return (
            <button
              key={key}
              onClick={() => handleIndexChange(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isSelected 
                  ? 'text-white shadow-lg' 
                  : 'bg-background hover:bg-muted text-foreground'
              }`}
              style={isSelected ? { backgroundColor: config.color } : {}}
              data-testid={`index-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{config.name}</span>
              {hasCachedData && (
                <span className={`w-2 h-2 rounded-full ${isSelected ? 'bg-white/50' : 'bg-green-500'}`} />
              )}
            </button>
          );
        })}
        
        {/* Info button */}
        <button
          onClick={() => setShowIndexInfo(true)}
          className="ml-auto flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition"
          data-testid="index-info-button"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="hidden sm:inline">What is this?</span>
        </button>
      </div>

      {/* Index Actions Row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <IndexIcon className="w-5 h-5" style={{ color: currentIndexConfig?.color }} />
          <span className="font-medium">{currentIndexConfig?.fullName}</span>
        </div>
        <button 
          onClick={() => handleFetchIndex(selectedIndex)}
          disabled={loadingIndex || refreshing}
          className="flex items-center gap-2 px-3 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition"
          style={{ backgroundColor: currentIndexConfig?.color }}
          data-testid="fetch-index-button"
        >
          <RefreshCw className={`w-4 h-4 ${(loadingIndex || refreshing) ? 'animate-spin' : ''}`} />
          {(loadingIndex || refreshing) ? 'Fetching...' : `Refresh ${currentIndexConfig?.name}`}
        </button>
      </div>

      {/* Index Info Modal */}
      {showIndexInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowIndexInfo(false)}>
          <div 
            className="bg-card rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-semibold">Understanding Vegetation Indices</h3>
              <button onClick={() => setShowIndexInfo(false)} className="p-2 hover:bg-muted rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {Object.entries(INDEX_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div 
                    key={key} 
                    className={`p-4 rounded-xl border-2 transition ${selectedIndex === key ? 'border-opacity-100' : 'border-transparent bg-muted/50'}`}
                    style={selectedIndex === key ? { borderColor: config.color, backgroundColor: `${config.color}10` } : {}}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${config.color}20` }}>
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                      <div>
                        <h4 className="font-semibold">{config.name}</h4>
                        <p className="text-sm text-muted-foreground">{config.fullName}</p>
                      </div>
                    </div>
                    <p className="text-sm mb-3">{config.description}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="font-medium text-muted-foreground">Formula:</span>
                        <code className="ml-2 px-2 py-1 bg-muted rounded text-xs">{config.formula}</code>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Best for:</span>
                        <span className="ml-2">{config.useCases.split(',')[0]}</span>
                      </div>
                    </div>
                    {/* Color Legend */}
                    <div className="mt-3 flex items-center gap-1 flex-wrap">
                      {config.palette.map((color, i) => (
                        <div key={i} className="flex items-center gap-1 text-xs">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                          <span className="text-muted-foreground">{config.labels[i]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Scout Mode Instructions */}
      {scoutMode && (
        <div className="card-floating" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, borderColor: 'rgba(245,158,11,0.3)' }}>
          <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--warning)' }} />
          <p style={{ color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--warning)' }}>Scout Mode Active:</strong> Tap anywhere on the map to add a scouting point. You can add photos, notes, and mark issues before saving.
          </p>
        </div>
      )}

      {/* Reading selector */}
      {ndviReadings && ndviReadings.length > 0 && (
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm">Select Date ({ndviReadings.length} readings):</span>
          <div className="flex items-center gap-2" ref={scrollContainerRef}>
            <button
              onClick={goToPrevReading}
              disabled={selectedReading === 0}
              className="p-1 hover:bg-muted rounded disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2 overflow-x-auto max-w-md">
              {ndviReadings.map((reading, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedReading(index)}
                  className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition ${
                    index === selectedReading
                      ? 'bg-primary text-white'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {getReadingDate(reading)?.toLocaleDateString() || 'No date'}
                  <span className="text-xs block opacity-70">{reading.source || 'Sentinel-2'}</span>
                </button>
              ))}
            </div>
            <button
              onClick={goToNextReading}
              disabled={selectedReading === ndviReadings.length - 1}
              className="p-1 hover:bg-muted rounded disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Stats cards - Index aware */}
      {currentReading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-floating" style={{ padding: 16 }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: 'var(--text-muted)' }}>
              <IndexIcon className="w-4 h-4" style={{ color: currentIndexConfig?.color }} />
              Mean {currentIndexConfig?.name}
            </div>
            <p className="text-2xl font-bold" style={{ color: currentIndexConfig?.color || status.color }}>
              {(currentReading.valid_pixels_pct !== undefined && currentReading.valid_pixels_pct < 10) 
                ? 'No data' 
                : (getReadingMean(currentReading)?.toFixed(3) || 'N/A')}
            </p>
          </div>
          <div className="card-floating" style={{ padding: 16 }}>
            <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>
              {selectedIndex === 'ndmi' ? 'Moisture Status' : 
               selectedIndex === 'ndre' ? 'Chlorophyll Status' : 'Vigor Status'}
            </div>
            <p className="text-2xl font-bold" style={{ color: currentIndexConfig?.color || status.color }}>
              {(currentReading.valid_pixels_pct !== undefined && currentReading.valid_pixels_pct < 10) 
                ? 'Unavailable' 
                : (currentIndexConfig?.statusText 
                    ? currentIndexConfig.statusText(getReadingMean(currentReading) || 0) 
                    : status.label)}
            </p>
          </div>
          <div className="card-floating" style={{ padding: 16 }}>
            <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{currentIndexConfig?.name} Range</div>
            <p className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
              {currentReading.min?.toFixed(3) || '0.000'} - {currentReading.max?.toFixed(3) || '0.000'}
            </p>
          </div>
          <div className="card-floating" style={{ padding: 16 }}>
            <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Acquisition Date</div>
            <p className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
              {getReadingDate(currentReading)?.toLocaleDateString() || 'N/A'}
            </p>
          </div>
        </div>
      )}
      
      {/* Data Quality Warning */}
      {currentReading && currentReading.valid_pixels_pct !== undefined && currentReading.valid_pixels_pct < 50 && (
        <div className="card-floating" style={{ 
          padding: 12, 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 10, 
          background: currentReading.valid_pixels_pct < 10 ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
          borderColor: currentReading.valid_pixels_pct < 10 ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'
        }}>
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" 
            style={{ color: currentReading.valid_pixels_pct < 10 ? '#ef4444' : '#f59e0b' }} />
          <div style={{ fontSize: 13 }}>
            <p style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>
              {currentReading.valid_pixels_pct < 10 
                ? 'No Usable Satellite Data' 
                : 'Reduced Data Quality'}
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              {currentReading.valid_pixels_pct < 10 
                ? 'The latest satellite imagery is completely obscured (likely snow or cloud cover in February). The index values shown are not reliable. Try again in a few days when clearer imagery becomes available.'
                : `Only ${Math.round(currentReading.valid_pixels_pct)}% of pixels are valid. Cloud cover or snow may be affecting accuracy. Values should be interpreted with caution.`}
            </p>
          </div>
        </div>
      )}

      {/* Overlay toggle */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showOverlay}
            onChange={(e) => setShowOverlay(e.target.checked)}
            className="rounded"
          />
          Show {currentIndexConfig?.name} Overlay
        </label>
        <span className="text-sm text-muted-foreground">
          {showOverlay ? (imageData ? 'Overlay visible' : 'No image data available') : 'Overlay hidden'}
        </span>
        {loadingImage && <Loader2 className="w-4 h-4 animate-spin" />}
      </div>

      {/* Index-specific Legend */}
      {showOverlay && currentIndexConfig && (
        <div className="card-floating p-4">
          <div className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            {currentIndexConfig.legendTitle}
          </div>
          <div className="flex flex-wrap gap-3">
            {currentIndexConfig.palette.map((color, i) => (
              <div key={i} className="flex items-center gap-2">
                <div 
                  className="w-5 h-5 rounded" 
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {currentIndexConfig.labels[i]}
                </span>
              </div>
            ))}
          </div>
          <div className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
            Range: {currentIndexConfig.clamp[0]} to {currentIndexConfig.clamp[1]}
          </div>
        </div>
      )}

      {/* No image warning */}
      {showOverlay && !imageData && !loadingImage && (
        <div className="card-floating" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8, borderColor: 'rgba(245,158,11,0.3)' }}>
          <AlertTriangle className="w-5 h-5" style={{ color: 'var(--warning)' }} />
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {currentIndexConfig?.name} image not available. Click "Refresh {currentIndexConfig?.name}" to fetch satellite data.
          </span>
        </div>
      )}

      {/* Map */}
      <div className="border rounded-lg overflow-hidden relative" style={{ height: '500px' }}>
        {loadingImage && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading NDVI image...</span>
            </div>
          </div>
        )}
        
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={16}
          options={mapOptions}
          onLoad={onMapLoad}
          onClick={handleMapClick}
        >
          {/* Existing scouting points */}
          {scoutingPoints.map(point => (
            <Marker
              key={point.id}
              position={{ lat: point.lat, lng: point.lng }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                scale: 10,
                fillColor: point.status === 'resolved' ? '#22c55e' : point.status === 'checked' ? '#3b82f6' : '#eab308',
                fillOpacity: point._pending ? 0.5 : 0.9,
                strokeColor: '#ffffff',
                strokeWeight: 2
              }}
              title={`Scouting point (${point.status})`}
            />
          ))}

          {/* Pending point marker */}
          {pendingScoutPoint && (
            <Marker
              position={{ lat: pendingScoutPoint.lat, lng: pendingScoutPoint.lng }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                scale: 14,
                fillColor: '#f59e0b',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3
              }}
            />
          )}

          {/* User location */}
          {userLocation && (
            <>
              <Circle
                center={{ lat: userLocation.lat, lng: userLocation.lng }}
                radius={userLocation.accuracy || 10}
                options={{
                  fillColor: '#4285F4',
                  fillOpacity: 0.15,
                  strokeColor: '#4285F4',
                  strokeOpacity: 0.3,
                  strokeWeight: 1
                }}
              />
              <Circle
                center={{ lat: userLocation.lat, lng: userLocation.lng }}
                radius={5}
                options={{
                  fillColor: '#4285F4',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2,
                  zIndex: 10
                }}
              />
            </>
          )}

          {/* Field polygon - clickable in scout mode */}
          {polygonPaths.length > 0 && (
            <Polygon
              paths={polygonPaths}
              options={{
                fillColor: scoutMode ? '#22c55e' : 'transparent',
                fillOpacity: scoutMode ? 0.1 : 0,  // Slight fill when in scout mode to capture clicks
                strokeColor: scoutMode ? '#22c55e' : '#ffffff',
                strokeOpacity: 0.8,
                strokeWeight: scoutMode ? 3 : 2,
                clickable: true,  // Always clickable
                zIndex: 2  // Above overlay
              }}
              onClick={handleMapClick}  // Forward clicks to map handler
            />
          )}

          {/* NDVI overlay - must not block clicks */}
          {showOverlay && imageData && bounds && (
            <GroundOverlay
              url={imageData}
              bounds={bounds}
              opacity={0.85}
              options={{
                clickable: false,  // Don't capture clicks - let them pass to polygon
                zIndex: 1  // Below polygon
              }}
            />
          )}
        </GoogleMap>

        {/* My Location button */}
        <button
          onClick={handleMyLocation}
          className="absolute bottom-4 right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 z-20"
          title="My Location"
        >
          <Navigation className="w-5 h-5 text-blue-600" />
        </button>
      </div>

      {/* Scouting Point Creation Modal */}
      {showScoutModal && pendingScoutPoint && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={handleCancelScoutPoint}>
          <div className="card-floating p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, color: 'var(--text)' }}>Add Scouting Point</h3>
            
            {/* Location */}
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--card-content-bg)', borderRadius: 10, border: '1px solid var(--border-soft)' }}>
              <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                <MapPin className="w-4 h-4" />
                <span>Location: {pendingScoutPoint.lat.toFixed(5)}, {pendingScoutPoint.lng.toFixed(5)}</span>
              </div>
            </div>

            {/* Checklist */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Issues Observed</label>
              <div className="grid grid-cols-2 gap-2">
                {CHECKLIST_ITEMS.map(item => (
                  <label
                    key={item.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: 10,
                      borderRadius: 10,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      background: scoutForm.checklist[item.key] ? 'rgba(198,170,88,0.15)' : 'var(--card-content-bg)',
                      border: scoutForm.checklist[item.key] ? '1px solid var(--gold)' : '1px solid var(--border-soft)'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={scoutForm.checklist[item.key]}
                      onChange={(e) => setScoutForm(prev => ({
                        ...prev,
                        checklist: { ...prev.checklist, [item.key]: e.target.checked }
                      }))}
                      className="sr-only"
                    />
                    <span>{item.icon}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.label}</span>
                    {scoutForm.checklist[item.key] && (
                      <Check className="w-4 h-4 ml-auto" style={{ color: 'var(--gold)' }} />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Notes</label>
              <textarea
                value={scoutForm.notes}
                onChange={(e) => setScoutForm(prev => ({ ...prev, notes: e.target.value }))}
                className="input-depth"
                rows={3}
                placeholder="Describe what you observed..."
              />
            </div>

            {/* Photos */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Photos</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                id="scout-photo-upload"
              />
              <label
                htmlFor="scout-photo-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: 16,
                  border: '2px dashed var(--border)',
                  borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                <Upload className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                <span style={{ color: 'var(--text-muted)' }}>Upload photos</span>
              </label>
              
              {scoutForm.photos.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {scoutForm.photos.map((photo, index) => (
                    <div key={index} className="relative w-20 h-20">
                      <img
                        src={photo.preview}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        style={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 20,
                          height: 20,
                          background: 'var(--danger)',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleCreateScoutPoint}
                disabled={creatingPoint}
                className="btn-depth-primary flex-1 flex items-center justify-center gap-2"
                style={{ opacity: creatingPoint ? 0.5 : 1 }}
              >
                {creatingPoint ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Point
                  </>
                )}
              </button>
              <button
                onClick={handleCancelScoutPoint}
                className="btn-depth-secondary"
                style={{ padding: '10px 24px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scouting points legend */}
      <div className="card-floating" style={{ padding: 16 }}>
        <h4 style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Scouting Points</h4>
        <div className="flex items-center gap-4 flex-wrap" style={{ fontSize: 13 }}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-400" />
            <span style={{ color: 'var(--text-secondary)' }}>Open</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span style={{ color: 'var(--text-secondary)' }}>Checked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span style={{ color: 'var(--text-secondary)' }}>Resolved</span>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="card-floating" style={{ padding: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--info)' }} />
        <div style={{ fontSize: 13 }}>
          <p style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>Understanding Remote Sensing</p>
          <p style={{ color: 'var(--text-secondary)' }}>
            Vegetation indices measure plant health from satellite imagery. 
            Use the index tabs above to switch between NDVI (general vigor), NDMI (moisture), 
            NDRE (chlorophyll), and EVI (dense canopy). Click "What is this?" for details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NDVIVisualization;
